import { Route, Switch } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import moment from 'moment';

import Sidebar, { SidebarItem } from '../components/Sidebar';
import { FloatingModal } from '../components/Modal';
import { getAchievements, getMyAchievements } from '../../../ducks/achievements';
import { getDevice, isMobile } from '../../../mobile/helpers';
import { getEvent } from '../../../ducks/event';
import { getImageUrl, navigate } from '../../../helpers';
import { getMaps } from '../../../ducks/maps';

import Achievements from './Achievements';
import Button from '../components/Button';
import Exhibitors from './Exhibitors';
import Food from './Food';
import Map from './Map';
import Maps from './Maps';
import Motes from './Motes';
import RavenErrorBoundary from '../../../components/RavenErrorBoundary';
import Schedule from './Schedule';
import Start from './Start';
import Streamers from './Streamers';
import Ticket from './Ticket';
import styles from './event.scss';
import withUser from '../../../components/withUser';

const propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.shape({
    url: PropTypes.string,
    path: PropTypes.string,
    params: PropTypes.shape({
      shortcode: PropTypes.string,
    }),
  }),
  event: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    shortcode: PropTypes.string,
    pub_exhibitors: PropTypes.array,
    activities: PropTypes.array,
    image_id: PropTypes.string,
    local_start_time: PropTypes.string,
    local_end_time: PropTypes.string,
    venue_str: PropTypes.string,
    location: PropTypes.string,
    url: PropTypes.string,
    purchase_url: PropTypes.string,
    seatmap_published: PropTypes.bool,
    hide_tickets: PropTypes.number,
    streamers: PropTypes.arrayOf(PropTypes.shape()),
  }),
  maps: PropTypes.shape({
    filter: PropTypes.func,
  }),
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired,
  }).isRequired,
  framed: PropTypes.bool.isRequired,
  achievements: PropTypes.shape(),
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
};

class Event extends React.Component {
  constructor() {
    super();
    this.state = {
      sidebarIsOpen: false,
      soonModalIsOpen: false,
    };
  }

  componentDidMount() {
    this.getEventData(this.props.match.params.shortcode);
  }

  componentDidUpdate(prevProps) {
    const { shortcode } = this.props.match.params;
    if (prevProps.match.params.shortcode !== shortcode) {
      this.getEventData(shortcode, true);
    }
  }

  getEventData = (shortcode, force) => {
    const { dispatch, user } = this.props;

    dispatch(getEvent(shortcode, force));
    dispatch(getMaps());
    dispatch(getAchievements(shortcode)).then(() => {
      if (user) {
        dispatch(getMyAchievements());
      }
    });
  };

  openMenu = () => {
    this.setState({
      sidebarIsOpen: true,
    });
  }

  closeMenu = () => {
    this.setState({
      sidebarIsOpen: false,
    });
  }

  goToStart = () => {
    const { history, match } = this.props;
    const shortcode = match && match.params && match.params.shortcode;

    this.closeMenu();
    navigate(history, `/app/event/${shortcode}`);
  }

  render() {
    const { sidebarIsOpen, soonModalIsOpen } = this.state;
    const {
      match,
      event,
      framed,
      history,
      achievements,
    } = this.props;
    const eventHasExhibitors = event && event.pub_exhibitors && event.pub_exhibitors.filter((pe) => ['exhibitor', 'sponsor'].includes(pe.type))?.length > 0;
    const eventHasFood = event && event.pub_exhibitors && event.pub_exhibitors.filter((pe) => pe.type === 'restaurant')?.length > 0;
    const eventHasMotes = (event?.id === '19be499d-af44-4a92-a8a4-d1f7ca8b1c43');

    const maps = this.props.maps
      .filter((map) => event && map.get('published') && map.get('event') === event.id);

    const routeProps = {
      onRequestOpenFn: !framed && (() => this.openMenu()),
      framed,
    };

    const routes = (
      <RavenErrorBoundary>
        <Switch>
          <Route path={`${match.path}/schedule/:id?`} render={(props) => <Schedule {...props} {...routeProps} />} />
          <Route exact path={`${match.path}/ticket`} render={(props) => <Ticket {...props} {...routeProps} />} />
          <Route path={`${match.path}/exhibitors`} render={(props) => (<Exhibitors {...props} {...routeProps} />)} />
          <Route exact path={`${match.path}/map/:node?`} render={(props) => <Map {...props} {...routeProps} />} />
          <Route exact path={`${match.path}/maps/:map?`} render={(props) => <Maps {...props} {...routeProps} />} />
          <Route path={`${match.path}/food`} render={(props) => (<Food {...props} {...routeProps} />)} />
          <Route path={`${match.path}/achievements/:chain`} render={(props) => (<Achievements {...props} {...routeProps} />)} />
          <Route path={`${match.path}/streamers`} render={(props) => (<Streamers {...props} {...routeProps} />)} />
          <Route path={`${match.path}/achievements`} render={(props) => (<Achievements {...props} {...routeProps} />)} />
          <Route path={`${match.path}/motes`} render={(props) => (<Motes {...props} {...routeProps} />)} />
          <Route path={`${match.path}/:option(ticker)?`} render={(props) => <Start {...props} {...routeProps} />} />
        </Switch>
      </RavenErrorBoundary>
    );

    if (match.path === '/frame/event/:shortcode?') {
      return routes;
    }

    return (
      <div
        className={styles.container}
      >
        <Sidebar
          open={sidebarIsOpen}
          onRequestCloseFn={() => this.closeMenu()}
        >
          <div
            className={styles.header}
            style={{ backgroundImage: event?.image_id ? `url(${getImageUrl(event.image_id)})` : undefined }}
          >
            <div className={styles.tint} />
            { event && (
              <>
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => this.goToStart()}
                  className={styles.title}
                >
                  {event.name}
                </div>
                <div className={styles.dates}>
                  {moment(event.local_start_time).format('D MMM')}
                  {' '}
                  –
                  {moment(event.local_end_time).format('D MMM')}
                </div>
                <div className={styles.dates}>
                  {event.venue_str}
                </div>
                <div className={styles.dates}>
                  {event.location}
                </div>
                <div
                  className={classNames(
                    styles.allEvents,
                    {
                      [styles.allEventsIos]: isMobile() && getDevice() === 'iOS',
                    },
                  )}
                >
                  <Button
                    outline
                    onClick={() => history.replace('/app')}
                    colors={{
                      text: '#fff',
                      border: '#fff',
                    }}
                  >
                    <i className="far fa-chevron-left" />
                    {' '}
                    Change event
                  </Button>
                </div>
              </>
            )}
          </div>
          <SidebarItem
            text="Start"
            to={`${match.url}`}
            icon="home"
          />
          <SidebarItem
            disabled={!(event && event.activities && event.activities?.length > 0)}
            text="Schedule"
            to={`${match.url}/schedule`}
            icon="calendar-alt"
          />
          <SidebarItem
            text="My tickets"
            to={`${match.url}/ticket`}
            icon="qrcode"
            disabled={(!event || (event && event.hide_tickets === 1))}
            onDisabledClick={() => this.setState({ soonModalIsOpen: true })}
          />
          { maps.size > 0 && (
            <SidebarItem
              text="Maps"
              to={`${match.url}/maps`}
              icon="map"
            />
          )}
          { achievements && achievements.size > 0 && (
            <SidebarItem
              text="Achievements"
              to={`${match.url}/achievements`}
              icon="trophy"
            />
          )}
          { eventHasExhibitors && (
            <SidebarItem
              text="Expo & partners"
              to={`${match.url}/exhibitors`}
              icon="store"
            />
          )}
          { eventHasMotes && (
            <SidebarItem
              text="Explore Jönköping"
              to={`${match.url}/motes`}
              icon="map-marked-alt"
            />
          )}
          { eventHasFood && (
            <SidebarItem
              text="Food & drinks"
              to={`${match.url}/food`}
              icon="utensils-alt"
            />
          )}
          { event && event.streamers && event.streamers?.length > 0 && (
            <SidebarItem
              text="Streamers"
              to={`${match.url}/streamers`}
              icon="tv"
            />
          )}
          <SidebarItem
            icon="ticket-alt"
            text="Buy tickets"
            href={event && event.purchase_url}
            disabled={!(event && event.purchase_url)}
            onDisabledClick={() => this.setState({ soonModalIsOpen: true })}
          />
          <SidebarItem
            icon="link"
            text="Read more"
            href={event && event.url}
            disabled={!(event && event.url)}
            onDisabledClick={() => this.setState({ soonModalIsOpen: true })}
          />
        </Sidebar>
        <FloatingModal
          isOpen={soonModalIsOpen}
          onRequestClose={() => this.setState({ soonModalIsOpen: false })}
          action={event && event.url && {
            onClick: () => { if (window) window.open(event.url); },
            text: 'Go to the website',
          }}
          buttonText="Close"
        >
          <div className={styles.notLive}>
            <div className={styles.notLiveTitle}>
              Soon™
            </div>
            <div className={styles.notLiveMessage}>
              We are working hard to get all the content up for
              {` ${event && event.name}`}
              , but are not there yet.
              {' '}
              { event && event.url
                ? (
                  <span>
                    In the meantime, make sure to get all the latest news on the
                    event website.
                  </span>
                )
                : <span>Make sure to come back closer to the event!</span>}
            </div>
          </div>
        </FloatingModal>

        <div className={styles.event}>
          {routes}
        </div>
      </div>
    );
  }
}

Event.propTypes = propTypes;

function mapStateToProps(state) {
  return {
    error: state.getIn(['event', 'error']),
    event: state.getIn(['event', 'data']),
    loading: state.getIn(['event', 'loading']),
    maps: state.getIn(['maps', 'list']),
    achievements: state.getIn(['achievements', 'achievements']),
  };
}

export default withUser(connect(mapStateToProps)(Event));
